import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams } from "react-router-dom";
import { qrJoinRoomFn } from "./firebase";
import { FUNCTION_URLS } from "./config/functions";
import { vibrate } from "./utils/platform";
import { requestLocation } from "./utils/location";
import { useAuth } from "./hooks/useAuth";
import { useUsername } from "./hooks/useUsername";
import { usePWA } from "./hooks/usePWA";
import { useCamera } from "./hooks/useCamera";
import { useRooms } from "./hooks/useRooms";
import { useAnalytics } from "./hooks/useAnalytics";
import { Header } from "./components/Header";
import { HomeScreen } from "./components/HomeScreen";
import { ChatRoom } from "./components/ChatRoom";
import { AdminPage } from "./pages/AdminPage";
import { InstallHelpModal } from "./components/InstallHelpModal";
import { InstallBanner } from "./components/InstallBanner";
import "./styles/main.scss";

/**
 * Main Layout Component - Handles all the business logic
 */
function AppLayout() {
  const navigate = useNavigate();
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("BlinkBli:installBannerHidden") === "true";
  });
  
  // --- Hooks ---
  const { 
    user, 
    authenticating, 
    signInAnonymouslyUser,
    ensureSignedIn 
  } = useAuth();
  
  const analytics = useAnalytics();

  // Page view tracking disabled - no Firebase calls on page load
  // To re-enable, uncomment the useEffect below
  /*
  useEffect(() => {
    analytics.logPageView(location.pathname, document.title);
  }, [location.pathname]);
  */
  
  const { 
    toast, 
    setToast, 
    showInstallHelp, 
    setShowInstallHelp, 
    showInstallBtn, 
    showHelpBtn, 
    isIOS, 
    isAndroid, 
    promptInstall,
    isInstalled
  } = usePWA();
  
  const { 
    cameraSupported, 
    cameraDenied, 
    scannerError, 
    scanning, 
    videoRef, 
    startCamera, 
    stopCamera, 
    setLastDetected 
  } = useCamera();
  
  const { rooms, loadingRooms, fetchRooms, addRoom } = useRooms(user, !!user);

  const { 
    username, 
    usernameText, 
    usernameNumber, 
    nameHint, 
    handleUsernameChange 
  } = useUsername({ 
    isAuthenticated: !!user, 
    userId: user?.uid,
    hasJoinedRoom: rooms.length > 0
  });

  const signInAttemptsRef = useRef(0);
  const [hasUrlCode, setHasUrlCode] = useState(false);

  // Check if there's a code in the URL on initial load
  useEffect(() => {
    const url = new URL(window.location.href);
    let code = url.searchParams.get('code');

    // Check for /c/:code format
    if (!code) {
      const pathMatch = url.pathname.match(/^\/c\/(\d{8})$/);
      if (pathMatch) {
        code = pathMatch[1];
      }
    }

    // Check for legacy code= format
    if (!code) {
      const pathMatch = url.pathname.match(/code=(\d{8})/);
      if (pathMatch) {
        code = pathMatch[1];
      }
    }

    setHasUrlCode(!!(code && /^\d{8}$/.test(code)));
  }, []);

  // Auto sign-in only when there's a valid code in the URL (8 digits)
  // This prepares the user to join the room without triggering on normal page loads
  useEffect(() => {
    // Only sign in if there's a valid code in the URL
    if (!hasUrlCode) return;
    if (user || authenticating || signInAttemptsRef.current >= 2) return;

    console.log(`Auto-signing in for room code... (attempt ${signInAttemptsRef.current + 1}/2)`);
    signInAttemptsRef.current += 1;
    signInAnonymouslyUser()
      .then((result) => {
        analytics.logAnonymousSignIn(result.success, signInAttemptsRef.current);
        // Set user ID for retention tracking
        if (result.success && result.user) {
          analytics.setUserId(result.user.uid);
        }
      })
      .catch((error) => {
        console.error('Auto sign-in failed:', error);
        analytics.logAnonymousSignIn(false, signInAttemptsRef.current);
        if (signInAttemptsRef.current >= 2) {
          console.log('Max sign-in attempts reached. Giving up.');
        }
      });
  }, [hasUrlCode, user, authenticating]);

  // Start camera when on home screen (only if no code in URL)
  useEffect(() => {
    if (!hasUrlCode) {
      startCamera();
    }
    return () => stopCamera();
  }, [hasUrlCode]);

  // --- Handlers ---
  const handleCodeVerification = async (code: string): Promise<{ lat: number; lng: number; accuracy?: number }> => {
    vibrate(15);
    setToast("Verifying code…");
    
    try {
      // Request GPS permission
      const location = await requestLocation();
      
      // Call verify-code endpoint
      const verifyResponse = await fetch(FUNCTION_URLS.verifyCode, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: code,
          gps: {
            lat: location.lat,
            lng: location.lng
          }
        })
      });
      
      const result = await verifyResponse.json();
      
      if (!result.ok) {
        throw new Error(result.reason || 'Verification failed');
      }
      
      console.log('Code verification successful:', result);
      
      // Sign in anonymously
      const signInResult = await signInAnonymouslyUser();
      if (!signInResult.success) {
        throw new Error('Failed to sign in');
      }
      
      // Set user ID for retention tracking
      if (signInResult.user) {
        analytics.setUserId(signInResult.user.uid);
      }
      
      setToast("Code verified ✅");
      
      // Clear URL params
      const url = new URL(window.location.href);
      url.searchParams.delete('code');
      window.history.replaceState({}, '', url.toString());
      
      return location;
      
    } catch (error: any) {
      console.error('Code verification error:', error);
      const reason = error?.code === 1
        ? "Location permission denied. GPS is required to verify the code."
        : error?.message || "Couldn't verify code. Please check your location.";
      
      setToast("Verification failed: " + reason);
      throw error;
    }
  };

  const handleJoinCode = async (code: string, method: "qr" | "manual" | "url" = "manual") => {
    vibrate(15);
    setLastDetected(code);
    setToast("Joining room…");
    
    analytics.logRoomJoinStarted(code, method);
    
    try {
      await ensureSignedIn();
      
      // Verify code and get location
      analytics.logLocationRequested();
      const location = await handleCodeVerification(code);
      analytics.logLocationGranted(location.accuracy);
      
      // After successful verification, join the room
      const deviceId = (localStorage.getItem("BlinkBli:deviceId") || crypto.randomUUID());
      localStorage.setItem("BlinkBli:deviceId", deviceId);
      
      await qrJoinRoomFn({ 
        code, 
        deviceId, 
        username, 
        location: { lat: location.lat, lng: location.lng },
        tenantId: "blinkbli" 
      });
      
      setToast("Joined room ✅");
      addRoom(code); // Save to localStorage immediately
      
      analytics.logRoomJoinSuccess(code);

      // Navigate to the chat view
      navigate(`/chat/${code}`);
    } catch (err: any) {
      const reason = err?.message || "Couldn't join room";
      setToast("Join failed: " + reason);
      analytics.logRoomJoinFailed(code, reason);
      throw err;
    }
  };

  const handleEnterRoom = (code: string) => {
    vibrate(10);
    navigate(`/chat/${code}`);
  };

  const checkLocationForRoom = async (roomCode: string): Promise<boolean> => {
    try {
      const location = await requestLocation();
      
      // Call verify-code to check if still in range
      const verifyResponse = await fetch(FUNCTION_URLS.verifyCode, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: roomCode,
          gps: {
            lat: location.lat,
            lng: location.lng
          }
        })
      });
      
      const result = await verifyResponse.json();
      return result.ok === true;
    } catch (err) {
      console.error("Location check failed:", err);
      return false;
    }
  };

  const showAuthGate = !user;

  const handleDismissBanner = () => {
    setBannerDismissed(true);
    analytics.logInstallBannerDismissed();
    try {
      localStorage.setItem("BlinkBli:installBannerHidden", "true");
    } catch {}
  };

  useEffect(() => {
    if (isInstalled) {
      setBannerDismissed(false);
      try {
        localStorage.removeItem("BlinkBli:installBannerHidden");
      } catch {}
    }
  }, [isInstalled]);

  const shouldShowInstallBanner = showHelpBtn && !isInstalled && !bannerDismissed;

  return (
    <>
      <Routes>
        {/* Home Screen */}
        <Route 
          path="/" 
          element={
            <>
              <Header
                usernameText={usernameText}
                usernameNumber={usernameNumber}
                nameHint={nameHint}
                onUsernameChange={handleUsernameChange}
                showAuthGate={showAuthGate}
                isAuthenticated={!!user}
                showInstallBtn={showInstallBtn}
                showHelpBtn={showHelpBtn}
                onPromptInstall={promptInstall}
                onShowInstallHelp={() => setShowInstallHelp(true)}
                hasJoinedRoom={rooms.length > 0}
              />
              {shouldShowInstallBanner && (
                <InstallBanner
                  canPromptInstall={showInstallBtn}
                  onPromptInstall={promptInstall}
                  onShowInstallHelp={() => setShowInstallHelp(true)}
                  onDismiss={handleDismissBanner}
                />
              )}
              <HomeScreen
                rooms={rooms}
                loadingRooms={loadingRooms}
                onFetchRooms={fetchRooms}
                onJoinCode={handleJoinCode}
                onEnterRoom={handleEnterRoom}
                cameraSupported={cameraSupported}
                cameraDenied={cameraDenied}
                scannerError={scannerError}
                scanning={scanning}
                videoRef={videoRef}
                hideCamera={hasUrlCode}
              />
            </>
          } 
        />

        {/* Code Join Route - /c/:code */}
        <Route 
          path="/c/:code" 
          element={
            <>
              <Header
                usernameText={usernameText}
                usernameNumber={usernameNumber}
                nameHint={nameHint}
                onUsernameChange={handleUsernameChange}
                showAuthGate={showAuthGate}
                isAuthenticated={!!user}
                showInstallBtn={showInstallBtn}
                showHelpBtn={showHelpBtn}
                onPromptInstall={promptInstall}
                onShowInstallHelp={() => setShowInstallHelp(true)}
                hasJoinedRoom={rooms.length > 0}
              />
              {shouldShowInstallBanner && (
                <InstallBanner
                  canPromptInstall={showInstallBtn}
                  onPromptInstall={promptInstall}
                  onShowInstallHelp={() => setShowInstallHelp(true)}
                  onDismiss={handleDismissBanner}
                />
              )}
              <HomeScreen
                rooms={rooms}
                loadingRooms={loadingRooms}
                onFetchRooms={fetchRooms}
                onJoinCode={handleJoinCode}
                onEnterRoom={handleEnterRoom}
                cameraSupported={cameraSupported}
                cameraDenied={cameraDenied}
                scannerError={scannerError}
                scanning={scanning}
                videoRef={videoRef}
                hideCamera={true}
              />
            </>
          } 
        />

        {/* Chat Room */}
        <Route 
          path="/chat/:roomCode" 
          element={
            <ChatRoomRoute
              user={user}
              onLocationCheck={checkLocationForRoom}
            />
          } 
        />

        {/* Admin Panel */}
        <Route 
          path="/admin" 
          element={<AdminPage user={user} />} 
        />

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Global Modals */}
      {toast && (
        <div className="toast">
          {toast}
        </div>
      )}

      {showInstallHelp && (
        <InstallHelpModal 
          isIOS={isIOS} 
          isAndroid={isAndroid} 
          hasNativePrompt={false} 
          onClose={()=>setShowInstallHelp(false)} 
        />
      )}
    </>
  );
}

/**
 * Chat Room Route Component
 */
function ChatRoomRoute({ 
  user, 
  onLocationCheck 
}: { 
  user: any;
  onLocationCheck: (roomCode: string) => Promise<boolean>;
}) {
  const { roomCode } = useParams<{ roomCode: string }>();
  const navigate = useNavigate();

  if (!roomCode) {
    return <Navigate to="/" replace />;
  }

  return (
    <ChatRoom 
      roomCode={roomCode}
      user={user}
      onBack={() => navigate('/')}
      onLocationCheck={() => onLocationCheck(roomCode)}
    />
  );
}

/**
 * App Component - Router wrapper
 */
export default function App() {
  return (
    <BrowserRouter>
      <div className="app">
        <AppLayout />
      </div>
    </BrowserRouter>
  );
}
